import { NgIf, NgClass, NgFor, AsyncPipe, LowerCasePipe } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, Inject, TemplateRef, ViewChild } from '@angular/core';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { AuthorizationSelectors } from '@next/core-lib/authorization';
import { OrderTypes, SiloDistributionRequirements } from '@next/core-lib/cart';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { FeatureFlags, FeatureFlagService } from '@next/core-lib/featureflag';
import { AgrI18nSharedModule } from '@next/core-lib/i18n';
import { SelectedCustomer, UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { map, Observable, of, switchMap, take } from 'rxjs';
import { FormatValidationMessagePipe } from 'src/app/cart/shared/pipes/format-validation-message.pipe';
import { AppState } from 'src/app/models/app.models';
import { Environment } from 'src/environments/environment.model';

@Component({
  templateUrl: './subrow-notifications.component.html',
  styleUrls: ['./subrow-notifications.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIf, MatDivider, MatIcon, NgClass, NgFor, AsyncPipe, LowerCasePipe, AgrI18nSharedModule, FormatValidationMessagePipe],
})
export class SubrowNotificationsComponent implements AfterViewInit {
  @ViewChild('template') template: TemplateRef<unknown>;

  public orderType?: OrderTypes;
  public isAtLeastAdvisor$: Observable<boolean> = this.store.select(AuthorizationSelectors.hasAtLeastAdvisorRole);
  public hasOrderRemarksPermission$: Observable<boolean> = this.store.select(AuthorizationSelectors.hasAtLeastCustomerRoleForApplicationKey('RemarksFeature'));
  public cartLineRemarksAvailable$: Observable<boolean>;
  public noSilosNeeded = SiloDistributionRequirements.notRelevant;
  public initCallback: () => void;

  protected readonly isUserAtLeastAdvisor$ = this.store.select(AuthorizationSelectors.hasAtLeastAdvisorRole);
  private readonly customer$: Observable<SelectedCustomer>;

  constructor(
    private readonly store: Store<AppState>,
    private readonly featureFlagService: FeatureFlagService,
    @Inject(EnvironmentInjector) public env: Environment,
  ) {
    this.customer$ = this.store.select(UserSelectors.selectCustomer);
  }

  ngAfterViewInit(): void {
    const featureFlags$ = this.customer$.pipe(
      map((selectedCustomer) => selectedCustomer?.shipTos[0]?.salesAreas[0]?.salesOrganization),
      switchMap((salesOrganization) => this.featureFlagService.getFeatureFlagsForSalesArea(salesOrganization)),
    );

    this.cartLineRemarksAvailable$ = this.isAtLeastAdvisor$.pipe(
      switchMap((isAtLeastAdvisor) =>
        featureFlags$.pipe(
          switchMap((featureFlags) => {
            if (isAtLeastAdvisor) {
              return of(featureFlags.includes(FeatureFlags.enableCartLineRemarksAdvisors));
            }
            return this.hasOrderRemarksPermission$.pipe(
              take(1),
              map(
                (isAtLeastCustomerForRemarksFeature) =>
                  featureFlags.includes(FeatureFlags.enableCartLineRemarksCustomers) || isAtLeastCustomerForRemarksFeature,
              ),
            );
          }),
        ),
      ),
    );
    this.initCallback();
  }

  doesLineSupportGrainPreSale(allowedOrderTypes: OrderTypes[]): boolean {
    return allowedOrderTypes.includes(OrderTypes.grainPreSalesOrder) && this.orderType !== OrderTypes.grainPreSalesOrder;
  }
}
